import { useQuery } from "@tanstack/react-query"
import { useGlobalState } from "../contexts/GlobalState"
import { useMsal } from "@azure/msal-react"
import type {AccountInfo} from "@azure/msal-browser"
import { InteractionRequiredAuthError } from "@azure/msal-browser"

const loginRequest = {
    scopes: ["openid", "profile", "email", "User.Read","User.Read.All","Group.Read.All"]    
}

interface AzureADSession {
    accessToken: string
    idToken: string
    account: AccountInfo
}

const getAuthenticatedSession = async (instance: any): Promise<AzureADSession> => {
    const account = instance.getActiveAccount()        
    if (!account) {
        throw new Error("No active account. Please sign in.")
    }

    try {
        // Try to acquire token silently  
        const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account
        })              
        return {
            accessToken: response.accessToken,
            idToken: response.idToken,
            account: response.account
        }
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            // If silent acquisition fails, trigger interactive login
            console.log("Silent token acquisition failed, acquiring via popup");
            const response = await instance.acquireTokenPopup(loginRequest)
            return {
                accessToken: response.accessToken,
                idToken: response.idToken,
                account: response.account
            }
        }
        throw error
    }
}

export const useAuthenticatedSession = () => {
    const { dispatch: globalStateDispatch } = useGlobalState()
    const { instance, accounts } = useMsal()
           
    return useQuery({
        queryKey: ["getAuthenticatedSession", accounts[0]?.homeAccountId],
        queryFn: () => getAuthenticatedSession(instance),
        enabled: accounts.length > 0, // Only run query if user is logged in
        onSuccess: (session: AzureADSession) => {
            try {
                // Parse the ID token to extract user claims
                const idTokenClaims = session.account.idTokenClaims as any
                
                globalStateDispatch({
                    type: "set_user_data",
                    data: {
                        userId: idTokenClaims.email?.toLowerCase() || idTokenClaims.preferred_username?.toLowerCase() || "",
                        firstName: idTokenClaims.name || "",
                        lastName: idTokenClaims.family_name || "",
                        accessToken: session.accessToken,
                    },
                })
            } catch (e) {
                console.error("Error getting authenticated session")
                console.error(e)
                globalStateDispatch({
                    type: "set_user_data",
                    data: {
                        userId: "",
                        firstName: "",
                        lastName: "",
                        accessToken: "",
                    },
                })
            }
        },
        onError: (e) => {
            console.error("Error getting authenticated session")
            console.error(e)
            globalStateDispatch({
                type: "set_user_data",
                data: {
                    userId: "",
                    firstName: "",
                    lastName: "",
                    accessToken: "",
                },
            })
        },
    })
}
